import React, { FC } from "react";
import classNames from "classnames";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "../ProfilingProtocolDetailPage.module.scss";

interface IVelocityZone {
  label: string;
  range: [number, number];
  description?: string;
}

/**
 * sez.4 MEDIA — outputType/dominio per parametrizzare titolo e unità zone.
 * - index endurance (P10/P11/P12/P13/P14): km/h, zone aerobiche da VMA/VIFT
 * - regression/fv_jump/fv_sprint — VBT/sled/salto: m/s, zone forza-velocità
 */
type ZoneDomain = "endurance" | "vbt";

interface IVelocityZonesProps {
  zones: IVelocityZone[];
  /** Dominio semantico: determina titolo e unità mostrate (sez.4 spec Profiling) */
  outputType?: string;
}

/** Mappa index 0-3 → classe CSS zona + colore label */
const ZONE_CLASSES: Array<{
  card: string;
  label: string;
  desc: string;
}> = [
  {
    card: style.zoneForza,
    label: style.zoneForza,
    desc: "Sviluppo forza massimale",
  },
  {
    card: style.zoneForzaVelocita,
    label: style.zoneForzaVelocita,
    desc: "Sviluppo forza-velocità",
  },
  {
    card: style.zonePotenza,
    label: style.zonePotenza,
    desc: "Sviluppo potenza",
  },
  {
    card: style.zoneVelocita,
    label: style.zoneVelocita,
    desc: "Sviluppo velocità",
  },
];

/**
 * Ricava il dominio semantico dall'outputType.
 * endurance → protocolli P10-P14 (index VMA/VIFT, km/h, zone aerobiche).
 * vbt → VBT/sled/salto/sprint (regression/fv_*, m/s, zone forza-velocità).
 */
function resolveDomain(outputType?: string): ZoneDomain {
  return outputType === "index" ? "endurance" : "vbt";
}

/** Configurazione titolo e unità per dominio (sez.4 MEDIA spec Profiling) */
const DOMAIN_CONFIG: Record<ZoneDomain, { title: string; unit: string }> = {
  endurance: {
    title: "Zone di velocità aerobiche",
    unit: "km/h",
  },
  vbt: {
    title: "Zone forza-velocità personalizzate",
    unit: "m/s",
  },
};

export const VelocityZones: FC<IVelocityZonesProps> = ({ zones, outputType }) => {
  if (!zones || zones.length === 0) return null;

  const domain = resolveDomain(outputType);
  const { title, unit } = DOMAIN_CONFIG[domain];

  return (
    <div className={style.card}>
      <div className={style.cardHeader}>
        {/* sez.4 MEDIA: titolo semantico parametrizzato per dominio */}
        <Typhography size="bold-16" style={{ color: "#00212c", margin: 0 }}>
          {title}
        </Typhography>
      </div>

      <div className={style.zonesGrid}>
        {zones.map((zone, i) => {
          const meta = ZONE_CLASSES[i] ?? {
            card: style.zoneFallback,
            label: style.zoneFallback,
            desc: "",
          };
          const description = zone.description ?? meta.desc;

          return (
            <div
              key={i}
              className={classNames(style.zoneCard, meta.card)}
            >
              <div className={style.zoneLabelRow}>
                <p className={classNames(style.zoneLabel, meta.label)}>
                  {zone.label}
                </p>
              </div>
              {/* sez.4 MEDIA: unità parametrica (km/h endurance, m/s VBT) */}
              <p className={style.zoneRange}>
                {zone.range[0]} – {zone.range[1]} {unit}
              </p>
              {description && (
                <p className={style.zoneDesc}>{description}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
